import { Alert, Box, Button, CircularProgress, keyframes, Portal, Stack, SvgIcon, Typography, useMediaQuery } from "@mui/material";
import { useCallback, useEffect, useEffectEvent, useRef, useState, type CSSProperties } from "react";
import puffin from "../../media/logo/platformpuffin.svg";
import { loadServiceLogos } from "./loadServiceLogos";
import { isTabActive, useActiveTab } from "./useActiveTab";

const puffinExplosionDuration = 550;
const burstDuration = 1600;

const explode = keyframes`
  0% { transform: scale(1); opacity: 1; }
  25% { transform: scale(1.2) rotate(-8deg); opacity: 1; }
  75%, 100% { transform: scale(1.8) rotate(12deg); opacity: 0; }
`;

const scatter = keyframes`
  from { transform: translate(-50%, -50%); }
  to { transform: translate(calc(-50% + var(--x)), -50%); }
`;

const launch = keyframes`
  from { transform: translateY(0) rotate(0deg) scale(0.4); }
  to { transform: translateY(var(--y)) rotate(var(--spin)) scale(1); }
`;

const fade = keyframes`
  0% { opacity: 0; }
  4%, 60% { opacity: 1; }
  100% { opacity: 0; }
`;

const unblur = keyframes`
  0%, 40% { backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
  100% { backdrop-filter: blur(0px); -webkit-backdrop-filter: blur(0px); }
`;

const completed = keyframes`
  0% { opacity: 0; transform: scale(0.6); }
  15% { opacity: 1; transform: scale(1.1); }
  25%, 60% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1); }
`;

interface Particle {
  src: string;
  x: number;
  y: number;
  spin: number;
  duration: number;
  delay: number;
}

const createParticles = (logos: string[]): Particle[] =>
  logos.map((src) => {
    // Explode in every direction, with fresh velocities and spin for each firing.
    const angle = Math.random() * Math.PI * 2;
    return {
      src,
      x: Math.cos(angle) * (24 + Math.random() * 28),
      y: Math.sin(angle) * (22 + Math.random() * 24),
      spin: (Math.random() - 0.5) * 1440,
      duration: 1100 + Math.random() * 300,
      delay: Math.random() * 160,
    };
  });

export interface ServiceLogoCannonProps {
  /** Start when the tab is visible and focused, without rendering the demo button. */
  autoPlay?: boolean;
  /** Called when the animation finishes or cannot be played. */
  onComplete?: () => void;
}

/** Show a centered puffin that loads, then explodes into one logo per service. */
export const ServiceLogoCannon = ({ autoPlay = false, onComplete }: ServiceLogoCannonProps) => {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const active = useActiveTab();
  const [status, setStatus] = useState<"idle" | "waiting" | "loading" | "burst" | "error">("idle");
  const [request, setRequest] = useState(0);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [puffinExploded, setPuffinExploded] = useState(false);
  const [message, setMessage] = useState("");
  const busy = useRef(false);
  const autoStarted = useRef(false);
  const complete = useEffectEvent(() => onComplete?.());

  useEffect(() => {
    if (status !== "burst" || !active) {
      return;
    }

    const completionTimer = window.setTimeout(() => setPuffinExploded(true), puffinExplosionDuration);
    const timer = window.setTimeout(() => {
      setParticles([]);
      setStatus("idle");
      setMessage("");
      busy.current = false;
      complete();
    }, burstDuration);

    return () => {
      window.clearTimeout(completionTimer);
      window.clearTimeout(timer);
    };
  }, [status, active]);

  const fire = useCallback(() => {
    if (busy.current) {
      return;
    }
    if (reducedMotion) {
      setMessage("Task completed! Animation is disabled by your reduced-motion preference.");
      onComplete?.();
      return;
    }

    busy.current = true;
    setStatus("waiting");
    setRequest((previous) => previous + 1);
  }, [reducedMotion, onComplete]);

  useEffect(() => {
    if (!request || !busy.current) return;

    if (!active) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Defer external completion playback while the browser tab is inactive.
      setStatus("waiting");
      return;
    }

    let cancelled = false;
    let loadingTimer: number | undefined;
    const start = async () => {
      setPuffinExploded(false);
      setStatus("loading");
      setMessage("Loading animation…");

      try {
        const minimumLoadingTime = new Promise<void>((resolve) => {
          loadingTimer = window.setTimeout(resolve, 750);
        });
        const [images] = await Promise.all([loadServiceLogos(), minimumLoadingTime]);
        if (cancelled || !isTabActive()) return;

        setParticles(createParticles(images));
        setStatus("burst");
        setMessage("Task completed!");
      } catch {
        if (cancelled) return;
        busy.current = false;
        setStatus("error");
        setMessage("");
        complete();
      }
    };

    void start();

    return () => {
      cancelled = true;
      window.clearTimeout(loadingTimer);
    };
  }, [request, active]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Start the externally requested completion animation after the host mounts it.
    if (autoPlay && !autoStarted.current) {
      autoStarted.current = true;
      fire();
    }
  }, [autoPlay, fire]);

  return (
    <Stack spacing={2} sx={{ alignItems: "center" }}>
      {!autoPlay && (
        <Button
          variant="contained"
          aria-busy={status === "loading"}
          disabled={status === "waiting" || status === "loading" || status === "burst"}
          onClick={() => {
            fire();
          }}
        >
          Show task completed animation
        </Button>
      )}
      {!autoPlay && message && (
        <Typography variant="body2" role="status">
          {message}
        </Typography>
      )}
      {!autoPlay && status === "error" && <Alert severity="error">Could not load the animation. Click the button to retry.</Alert>}
      {(status === "loading" || status === "burst") && active && !reducedMotion && (
        <Portal>
          <Box data-tid="service-logo-cannon" sx={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: "snackbar" }}>
            <Box
              aria-hidden="true"
              data-tid="service-logo-backdrop"
              sx={{
                position: "absolute",
                inset: 0,
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
                animation: status === "burst" ? `${unblur} ${burstDuration}ms linear both` : undefined,
              }}
            />
            <Box sx={{ position: "absolute", left: "50%", top: "50%", width: 192, height: 192, transform: "translate(-50%, -50%)" }}>
              {puffinExploded ? (
                <SvgIcon
                  data-tid="service-logo-completed"
                  aria-hidden="true"
                  sx={{ position: "absolute", inset: 32, width: 128, height: 128, color: "success.main", animation: `${completed} 800ms ease-out both` }}
                >
                  <path d="M9 16.17 4.83 12 3.41 13.41 9 19 21 7 19.59 5.59Z" />
                </SvgIcon>
              ) : (
                <Box
                  component="img"
                  data-tid="service-logo-puffin"
                  src={puffin}
                  alt=""
                  draggable={false}
                  sx={{
                    position: "absolute",
                    inset: 32,
                    width: 128,
                    height: 128,
                    animation: status === "burst" ? `${explode} ${puffinExplosionDuration}ms ease-out both` : undefined,
                  }}
                />
              )}
              {status === "loading" && <CircularProgress size={192} thickness={2} aria-label="Loading animation" sx={{ position: "absolute", inset: 0 }} />}
            </Box>
            {status === "burst" && (
              <Box aria-hidden="true" data-tid="service-logo-particles">
                {particles.map((particle, index) => (
                  <Box
                    key={index}
                    component="span"
                    style={{ "--x": `${particle.x}vw` } as CSSProperties}
                    sx={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      width: 40,
                      height: 40,
                      animation: `${scatter} ${particle.duration}ms ${particle.delay}ms cubic-bezier(0.15, 0.5, 0.4, 1) both, ${fade} ${particle.duration}ms ${particle.delay}ms linear both`,
                    }}
                  >
                    <Box
                      component="img"
                      src={particle.src}
                      alt=""
                      draggable={false}
                      style={{ "--y": `${particle.y}vh`, "--spin": `${particle.spin}deg` } as CSSProperties}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        // Separate horizontal and vertical easing creates a smooth arc without a falling phase.
                        animation: `${launch} ${particle.duration}ms ${particle.delay}ms cubic-bezier(0.1, 0.75, 0.35, 1) both`,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Portal>
      )}
    </Stack>
  );
};
